/**
 * Types + loader for Phase A AI Agent intent catalog (mirrors backend agent-intent-catalog.json).
 * Fuzzy/LLM resolution wires in later phases; keep this in sync with backend resources.
 */
import catalogJson from '../data/agentIntentCatalog.json';

export type AgentIntentOutcome = 'form' | 'project_pick' | 'navigate' | 'unknown';

export type AgentIntentDefinition = {
  id: string;
  label: string;
  outcome: AgentIntentOutcome;
  formVariant: string | null;
  backendAction: string | null;
  hidden: boolean;
  phrases: string[];
  modeHints: Record<string, string[]>;
  contextRequiredPhrases: string[];
  negativePhrases: string[];
};

export type AgentIntentCatalogDocument = {
  version: number;
  description?: string;
  intents: AgentIntentDefinition[];
};

export type AgentIntentCandidate = {
  intentId: string;
  label: string;
  score: number;
  matchedPhrase?: string;
};

export type AgentIntentResolution = {
  intentId: string;
  label: string;
  outcome: AgentIntentOutcome;
  formVariant?: string | null;
  backendAction?: string | null;
  mode?: 'new' | 'existing';
  score: number;
  source: 'fuzzy' | 'llm' | 'confirm' | 'explicit' | 'unknown';
  needsConfirmation?: boolean;
  contextRequired?: boolean;
  reply?: string;
  normalizedInput?: string;
  candidates?: AgentIntentCandidate[];
};

export const AGENT_INTENT_CATALOG = catalogJson as AgentIntentCatalogDocument;

export function getAgentIntentById(id: string): AgentIntentDefinition | undefined {
  return AGENT_INTENT_CATALOG.intents.find((i) => i.id === id);
}

export function getMatchableAgentIntents(): AgentIntentDefinition[] {
  // Include hidden classifiable intents (run-flow, etc.); UI pickers filter on `hidden`.
  return AGENT_INTENT_CATALOG.intents.filter(
    (i) => i.id !== 'unknown' && Array.isArray(i.phrases) && i.phrases.length > 0,
  );
}

/** Align with backend AgentIntentCatalogService.normalizeInput */
export function normalizeAgentIntentInput(raw: string | null | undefined): string {
  if (!raw) return '';
  return raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[^a-z0-9\s/\-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
